function debounce(fn, wait) {
  let t;

  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');

  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');

    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');

    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }
  }

  static updateBodyFacetState(searchParams = window.location.search.slice(1)) {
    const params = new URLSearchParams(searchParams);

    const hasActiveFacets = Array.from(params.entries()).some(([key, value]) => {
      if (!value) return false;

      return (
        key.startsWith('filter.') ||
        key.startsWith('filter_') ||
        key === 'min_price' ||
        key === 'max_price'
      );
    });

    document.body.classList.toggle('facets-active', hasActiveFacets);
  }

  static setMobileFilterTriggerListener() {
    const filterTrigger = document.querySelector('.mobile-facets__disclosure');

    if (filterTrigger) {
      filterTrigger.addEventListener('click', function () {
        document.body.classList.add('sidebar-filter-enabled');
      });
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;

      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.updateBodyFacetState(searchParams);
      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.updateBodyFacetState(searchParams);

    const sections = FacetFiltersForm.getSections();

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    document
      .getElementById('ProductGridContainer')
      .querySelector('.collection')
      .classList.add('loading');

    if (countContainer) {
      countContainer.classList.add('loading');
    }

    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }

    document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;

        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const newGrid = parsedHTML.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');

    if (!newGrid || !currentGrid) return;

    currentGrid.innerHTML = newGrid.innerHTML;

    if (typeof initDeferredMedia === 'function') {
      initDeferredMedia(currentGrid);
    }

    document.dispatchEvent(
      new CustomEvent('compare:products-rendered')
    );
  }

  static renderProductCount(html) {
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      const parsedContainer = new DOMParser()
        .parseFromString(html, 'text/html')
        .getElementById('ProductCount');

      if (parsedContainer) {
        container.innerHTML = parsedContainer.innerHTML;
      }

      container.classList.remove('loading');
    }

    if (containerDesktop) {
      const parsedContainerDesktop = new DOMParser()
        .parseFromString(html, 'text/html')
        .getElementById('ProductCountDesktop');

      if (parsedContainerDesktop) {
        containerDesktop.innerHTML = parsedContainerDesktop.innerHTML;
      }

      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const matchesIndex = (element) => {
      const jsFilter = event
        ? event.target.closest('.js-filter')
        : undefined;

      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );

      if (targetElement) {
        targetElement.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.setMobileFilterTriggerListener();

    if (countsToRender) {
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest('.js-filter')
      );
    }
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach(
      (selector) => {
        const activeFacetsElement = html.querySelector(selector);
        const targetElement = document.querySelector(selector);

        if (!activeFacetsElement || !targetElement) return;

        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    );

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach(
      (selector) => {
        const sourceElement = html.querySelector(selector);
        const targetElement = document.querySelector(selector);

        if (!sourceElement || !targetElement) return;

        targetElement.innerHTML = sourceElement.innerHTML;
      }
    );
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    const targetElementAccessibility =
      target.querySelector('.facets__summary');

    const sourceElementAccessibility =
      source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      targetElementAccessibility.outerHTML =
        sourceElementAccessibility.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${
        searchParams && '?'.concat(searchParams)
      }`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);

    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll(
      'facet-filters-form form'
    );

    if (event.srcElement.className === 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(
        event.target.closest('form')
      );

      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];

      const isMobile =
        event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            [
              'FacetSortForm',
              'FacetFiltersForm',
              'FacetSortDrawerForm',
            ].includes(form.id)
          ) {
            document
              .querySelectorAll('.no-js-list')
              .forEach((el) => el.remove());

            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });

      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();

    FacetFiltersForm.toggleActiveFacets();

    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1
          );

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];

FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);

FacetFiltersForm.searchParamsPrev =
  window.location.search.slice(1);

customElements.define(
  'facet-filters-form',
  FacetFiltersForm
);

FacetFiltersForm.updateBodyFacetState();
FacetFiltersForm.setListeners();

document.body.addEventListener('click', function (event) {
  const trigger = event.target.closest(
    '.mobile-facets__disclosure'
  );

  if (trigger) {
    document.body.classList.add('sidebar-filter-enabled');
  }

  if (event.target.classList.contains('filter-overlay')) {
    document.body.classList.remove('sidebar-filter-enabled');

    document
      .querySelector('.mobile-facets__disclosure')
      ?.removeAttribute('open');
  }
});

document.querySelectorAll('.closeFilterButton').forEach((button) => {
  button.addEventListener('click', () => {
    document.body.classList.remove('sidebar-filter-enabled');
  });
});